import { useEffect, useRef, useState } from "react";
import AgoraRTC from "agora-rtc-sdk-ng";
import { FaArrowLeft, FaMicrophone, FaMicrophoneSlash, FaVideo, FaVideoSlash, FaSync, FaPhone, FaVolumeUp, FaVolumeXmark } from "react-icons/fa";
import Draggable from "react-draggable";
import {
    GetAgoraToken
} from "../services/api";
import { useSelector, useDispatch } from "react-redux";
import { socket } from "../socket/socket";
import { CALL_EVENTS } from "../socket/socketEvents";
import { resetCallState } from "../features/call/callSlice";
import CallAudioManager from "../utils/CallAudioManager";
import { showError } from "../utils/toast";
import { getProfileImage } from "../utils/helpers";

export default function VideoCall() {
    const appId = process.env.REACT_APP_AGORA_APP_ID;

    const dispatch = useDispatch();
    const call = useSelector((state) => state.call);

    const initStartedRef = useRef(false);
    const joinedRef = useRef(false);

    const client = useRef(null);
    const localVideoTrack = useRef(null);
    const localAudioTrack = useRef(null);

    const localContainer = useRef(null);
    const [remoteUsers, setRemoteUsers] = useState({});
    const [isAudioMuted, setIsAudioMuted] = useState(false);
    const [isVideoMuted, setIsVideoMuted] = useState(false);
    const [isSpeakerOn, setIsSpeakerOn] = useState(false);
    const [hasMultipleCameras, setHasMultipleCameras] = useState(false);

    const [remoteVideoStoppedByUser, setRemoteVideoStoppedByUser] = useState(false);
    const [hasRemoteEverPublished, setHasRemoteEverPublished] = useState(false);
    const [lastRemoteFrame, setLastRemoteFrame] = useState(null);

    const [callDuration, setCallDuration] = useState(0);
    const timerIntervalRef = useRef(null);
    const heartbeatIntervalRef = useRef(null);
    const remoteVideoMuteIntent = useRef({});

    const draggableRef = useRef(null);

    // Initialize Agora Client
    useEffect(() => {
        if (!call) return;
        if (joinedRef.current) return;

        if (initStartedRef.current) {
            return;
        }

        initStartedRef.current = true;

        AgoraRTC.setLogLevel(4);

        client.current = AgoraRTC.createClient({
            mode: "rtc",
            codec: "vp8",
        });

        setupEventListeners();

        let cancelled = false;

        (async () => {
            try {
                const { data } = await GetAgoraToken({
                    callId: call.callId,
                    role: call.role,
                });

                if (cancelled) return; // 🛑 CRITICAL

                await joinChannel({
                    token: data.token,
                    channel: data.channel,
                    uid: data.uid,
                });
            } catch (err) {
                if (!cancelled) {
                    console.error("Agora init failed", err);
                }
            }
        })();

        return () => {
            cancelled = true;
            leaveChannel();
        };
    }, [call?.status]);

    useEffect(() => {
        if (!call?.startedAt) return;

        const handleBeforeUnload = (e) => {
            e.preventDefault();
            e.returnValue = ""; // required for Chrome
        };

        window.addEventListener("beforeunload", handleBeforeUnload);

        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, [call?.startedAt]);

    useEffect(() => {
        if (!call?.startedAt) return;

        const startedAtMs = new Date(call.startedAt).getTime();

        const updateTimer = () => {
            const now = Date.now();
            const diffSeconds = Math.floor((now - startedAtMs) / 1000);
            setCallDuration(Math.max(0, diffSeconds));
        };

        updateTimer();
        timerIntervalRef.current = setInterval(updateTimer, 1000);

        return () => {
            clearInterval(timerIntervalRef.current);
            timerIntervalRef.current = null;
        };
    }, [call?.startedAt]);

    const formatTime = (seconds = 0) => {
        const safeSeconds = Math.max(0, seconds);
        const m = Math.floor(safeSeconds / 60);
        const s = safeSeconds % 60;
        return `${m}:${s.toString().padStart(2, "0")}`;
    };

    useEffect(() => {
        if (!call?.startedAt || !call?.heartbeat_timer) return;

        const intervalMs = call?.heartbeat_timer * 1000;

        heartbeatIntervalRef.current = setInterval(() => {
            socket.emit(CALL_EVENTS.CALL_HEARTBEAT, {
                callId: call?.callId,
            });
        }, intervalMs);

        return () => {
            clearInterval(heartbeatIntervalRef.current);
            heartbeatIntervalRef.current = null;
        };
    }, [call?.startedAt, call?.heartbeat_timer]);

    // Setup event listeners
    const setupEventListeners = () => {

        client.current.on("connection-state-change", (curState) => {

            if (curState === "CONNECTED" && call?.status === "outgoing") {
                CallAudioManager.playOutgoing();
            }
        });

        client.current.on("user-published", async (user, mediaType) => {

            remoteVideoMuteIntent.current[user.uid] = false;
            await client.current.subscribe(user, mediaType);

            CallAudioManager.stopAll();

            socket.emit(CALL_EVENTS.CALL_HEARTBEAT, {
                callId: call?.callId,
            });

            if (mediaType === "video") {
                setHasRemoteEverPublished(true);
                setRemoteVideoStoppedByUser(false);

                setRemoteUsers((prev) => ({
                    ...prev,
                    [user.uid]: user.videoTrack,
                }));
            }

            if (mediaType === "audio") {
                user.audioTrack.setVolume(100);
                user.audioTrack.play();
            }
        });

        client.current.on("user-unpublished", (user, mediaType) => {

            if (mediaType === "video") {

                const wasIntentional = remoteVideoMuteIntent.current[user.uid] === true;

                console.log("wasIntentional ", wasIntentional);

                if (wasIntentional) {
                    setRemoteVideoStoppedByUser(true);
                } else {
                    const track = remoteUsers[user.uid];
                    if (track) {
                        const frame = captureLastFrame(track);
                        if (frame) setLastRemoteFrame(frame);
                    }
                }

                setRemoteUsers(prev => {
                    const updated = { ...prev };
                    delete updated[user.uid];
                    return updated;
                });
            }

            if (mediaType === "audio") {
                user.audioTrack?.stop();
            }
        });

        client.current.on("user-left", (user) => {
            user.audioTrack?.stop();

            setRemoteUsers((prev) => {
                const updated = { ...prev };
                delete updated[user.uid];
                return updated;
            });
        });

        client.current.on("user-info-updated", (uid, msg) => {

            if (msg === "mute-video") {
                remoteVideoMuteIntent.current[uid] = true;
            }

            if (msg === "unmute-video") {
                remoteVideoMuteIntent.current[uid] = false;
            }
        });
    };

    // Join channel
    const joinChannel = async ({ token, channel, uid }) => {
        if (!client.current) return;

        if (joinedRef.current) {
            return;
        }

        if (client.current.connectionState !== "DISCONNECTED") {
            return;
        }

        joinedRef.current = true;

        try {
            await client.current.join(appId, channel, token, uid);

            if (!joinedRef.current) return;

            localAudioTrack.current = await AgoraRTC.createMicrophoneAudioTrack();
            localVideoTrack.current = await AgoraRTC.createCameraVideoTrack();

            await localAudioTrack.current.setEnabled(true);

            if (!joinedRef.current) return;

            localVideoTrack.current.play(localContainer.current);

            await client.current.publish([
                localAudioTrack.current,
                localVideoTrack.current,
            ]);

            const cameras = await AgoraRTC.getCameras();
            setHasMultipleCameras(cameras.length > 1);
        } catch (err) {
            joinedRef.current = false;
            console.error(err);
        }
    };

    // Leave channel
    const leaveChannel = async () => {
        if (!client.current) return;

        try {
            localAudioTrack.current?.stop();
            localAudioTrack.current?.close();
            localAudioTrack.current = null;

            localVideoTrack.current?.stop();
            localVideoTrack.current?.close();
            localVideoTrack.current = null;

            await client.current.leave();
            await client.current.removeAllListeners();
        } catch (e) {
            console.warn("Leave error", e);
        } finally {
            joinedRef.current = false;
            setRemoteUsers({});
        }
    };

    const toggleAudio = async () => {
        if (!localAudioTrack.current) return;

        await localAudioTrack.current.setMuted(!isAudioMuted);
        setIsAudioMuted(prev => !prev);
    };

    const toggleVideo = async () => {
        if (!localVideoTrack.current) return;

        await localVideoTrack.current.setMuted(!isVideoMuted);
        setIsVideoMuted(prev => !prev);
    };

    const toggleSpeaker = async () => {
        const audio = document.getElementById("remote-audio");

        if (!audio?.setSinkId) {
            showError("Output device switching is not supported in this browser");
            return;
        }

        const devices = await navigator.mediaDevices.enumerateDevices();
        const audioOutputs = devices.filter(d => d.kind === "audiooutput");

        // Default output = speaker
        const defaultDevice = audioOutputs[0];

        if (isSpeakerOn) {
            // Turn speaker OFF → use a different output if available
            const otherDevice = audioOutputs.find(d => d.deviceId !== defaultDevice.deviceId);
            if (otherDevice) {
                await audio.setSinkId(otherDevice.deviceId);
            }
        } else {
            // Turn speaker ON → use default output (speaker)
            await audio.setSinkId(defaultDevice.deviceId);
        }

        setIsSpeakerOn(!isSpeakerOn);
    };

    const switchCamera = async () => {
        if (!localVideoTrack.current) return;

        try {
            await localVideoTrack.current.switchDevice();
        } catch (error) {
            console.log(error);
        }
    };

    const OnEndCall = async () => {

        clearInterval(timerIntervalRef.current);
        clearInterval(heartbeatIntervalRef.current);

        timerIntervalRef.current = null;
        heartbeatIntervalRef.current = null;

        await leaveChannel();

        joinedRef.current = false;
        initStartedRef.current = false;

        if (callDuration > 0) {
            socket.emit(CALL_EVENTS.CALL_END, {
                callId: call?.callId,
            });
        }
        else {
            socket.emit(CALL_EVENTS.CALL_CANCEL, {
                callId: call?.callId,
            });
        }

        dispatch(resetCallState());
    }

    return (
        <div className="fixed inset-0 bg-black z-[9999] flex justify-center items-center">

            <button
                onClick={OnEndCall}
                className="
                    absolute top-4 left-4 z-[10000]
                    bg-white/10 hover:bg-white/20
                    p-3 rounded-full
                "
            >
                <FaArrowLeft className="text-white text-xl" />
            </button>

            {/* WRAPPER for WEB */}
            <div className="
                    relative 
                    w-full h-full 
                    md:w-[40%]
                    bg-black 
                    md:overflow-hidden
                ">

                {call?.startedAt && (
                    <div className="
                    absolute top-4 right-4 
                    z-50
                    text-white text-sm 
                    bg-black/60 backdrop-blur
                    px-3 py-1 rounded-full
                ">
                        {formatTime(callDuration)}
                    </div>
                )}

                <Draggable
                    bounds="parent"
                    nodeRef={draggableRef}   // ✅ IMPORTANT
                >
                    {/* LOCAL VIDEO */}
                    <div
                        ref={(el) => {
                            draggableRef.current = el;  // for Draggable
                            localContainer.current = el; // for Agora
                        }}
                        className="
                            w-[120px] h-[160px]
                            md:w-[150px] md:h-[200px]
                            rounded-xl overflow-hidden bg-black
                            absolute bottom-24 right-4 
                            shadow-xl border border-white/10 z-20
                        "
                    ></div>
                </Draggable>

                {/* LIVE REMOTE VIDEO */}
                {Object.keys(remoteUsers).length > 0 &&
                    Object.entries(remoteUsers).map(([uid, videoTrack]) => (
                        <RemoteVideo key={uid} uid={uid} videoTrack={videoTrack} />
                    ))
                }

                {/* USER TURNED CAMERA OFF INTENTIONALLY */}
                {Object.keys(remoteUsers).length === 0 &&
                    hasRemoteEverPublished &&
                    remoteVideoStoppedByUser && (
                        <VideoCallConnecting
                            name={call?.remote?.name}
                            age={call?.remote?.age}
                            country={call?.remote?.country}
                            flag={call?.remote?.flag}
                            profileImg={getProfileImage(call?.remote?.profileImg)}
                            bgImg={call?.remote?.profileImg}
                            mode="camera-off"
                        />
                    )}

                {/* NETWORK ISSUE → SHOW LAST FRAME */}
                {Object.keys(remoteUsers).length === 0 &&
                    hasRemoteEverPublished &&
                    !remoteVideoStoppedByUser &&
                    lastRemoteFrame && (
                        <img
                            src={lastRemoteFrame}
                            className="absolute inset-0 w-full h-full object-cover"
                            alt="Last frame"
                        />
                    )}

                {/* NEVER PUBLISHED → CONNECTING */}
                {Object.keys(remoteUsers).length === 0 &&
                    !hasRemoteEverPublished && (
                        <VideoCallConnecting
                            name={call?.remote?.name}
                            age={call?.remote?.age}
                            country={call?.remote?.country}
                            flag={call?.remote?.flag}
                            profileImg={getProfileImage(call?.remote?.profileImg)}
                            bgImg={getProfileImage(call?.remote?.profileImg)}
                        />
                    )}

                <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30">

                    {/* Background Box */}
                    <div className="flex items-center gap-4 bg-black/40 backdrop-blur-xl px-6 py-3 rounded-2xl shadow-lg">

                        {/* AUDIO */}
                        <button
                            onClick={toggleAudio}
                            className="w-10 h-10 bg-white rounded-full flex items-center justify-center text-black text-xl transition active:scale-90"
                        >
                            {isAudioMuted ? <FaMicrophoneSlash /> : <FaMicrophone />}
                        </button>

                        {/* VIDEO */}
                        <button
                            onClick={toggleVideo}
                            className="w-10 h-10 bg-white rounded-full flex items-center justify-center text-black text-xl transition active:scale-90"
                        >
                            {isVideoMuted ? <FaVideoSlash /> : <FaVideo />}
                        </button>

                        {/* SPEAKER */}
                        {/* <button
                            onClick={toggleSpeaker}
                            className="w-10 h-10 bg-white rounded-full flex items-center justify-center text-black text-xl transition active:scale-90"
                        >
                            {isSpeakerOn ? <FaVolumeUp /> : <FaVolumeUp />}
                        </button> */}

                        {/* SWITCH CAMERA (only if available) */}
                        {hasMultipleCameras && (
                            <button
                                onClick={switchCamera}
                                className="w-10 h-10 bg-white rounded-full flex items-center justify-center text-black text-xl transition active:scale-90"
                            >
                                <FaSync />
                            </button>
                        )}

                        {/* END CALL */}
                        <button
                            onClick={OnEndCall}
                            className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center shadow-lg active:scale-90 transition"
                        >
                            <FaPhone
                                className="text-white"
                                size={16}
                                style={{ transform: "rotate(225deg)" }}
                            />
                        </button>

                    </div>

                </div>

            </div>

        </div>
    );
}

// Remote User Component
function RemoteVideo({ uid, videoTrack }) {
    const container = useRef(null);

    useEffect(() => {
        if (videoTrack) videoTrack.play(container.current);
    }, [videoTrack]);

    return (
        <div
            ref={container}
            className="absolute top-0 left-0 w-full h-full bg-black"
        ></div>
    );
}

function VideoCallConnecting({
    name,
    age,
    country,
    flag,
    profileImg,
    bgImg,
    mode
}) {
    return (
        <div className="relative w-full h-screen overflow-hidden bg-black text-white">

            {/* Background Blur Image */}
            <img
                src={bgImg}
                alt="background"
                className="absolute inset-0 w-full h-full object-cover opacity-60 blur-md scale-110"
            />

            {/* Dim overlay */}
            <div className="absolute inset-0 bg-black/40" />

            {/* Connecting... */}
            {
                mode !== "camera-off" &&
                <div className="absolute top-10 left-1/2 -translate-x-1/2 bg-black/50 px-6 py-2 rounded-full text-sm flex items-center gap-2">

                    {/* Loader */}
                    <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>

                    Connecting...
                </div>
            }

            {/* Center Content */}
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 -translate-y-20">

                {/* Round Profile Image */}
                <div className="w-28 h-28 rounded-full overflow-hidden border-4 border-white shadow-lg">
                    <img src={profileImg} alt="profile" className="w-full h-full object-cover" />
                </div>

                {/* User Name + Age */}
                <h2 className="text-xl font-semibold">
                    {name}, {age}
                </h2>

                {/* Country */}
                <p className="flex items-center justify-center gap-2 text-sm opacity-80">
                    <img
                        src={flag}
                        alt="flag"
                        className="w-6 h-4 rounded-sm"
                    />
                    {country}
                </p>
            </div>

        </div>
    );
}

const captureLastFrame = (videoTrack) => {
    const videoElement = videoTrack.getMediaStreamTrack()
        ? videoTrack._player?.video
        : null;

    if (!videoElement) return null;

    const canvas = document.createElement("canvas");
    canvas.width = videoElement.videoWidth;
    canvas.height = videoElement.videoHeight;

    const ctx = canvas.getContext("2d");
    ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);

    return canvas.toDataURL("image/jpeg", 0.7);
};